/**
 * Compiled-path hydration cursor (SSR-PLAN pieces 157/158).
 * Spec: tasks/roadmap/COMPILED-HYDRATION-SPEC.md. Contract: docs/SSR.md.
 *
 * Under the plugin `ssr` option, codegen imports these $h creation helpers
 * instead of the plain ones, so compiled components CLAIM the server's DOM
 * nodes (in one flat pre-order pass, which is exactly compiled creation
 * order) instead of recreating them. Zero-k stays intact by construction:
 * default codegen never references this module.
 *
 * Bail-safe: the first structural mismatch deactivates claiming with one
 * warning; every subsequent call falls through to plain creation, so the
 * worst case is pre-hydration behavior (fresh DOM), never a corrupted tree.
 */
import { registerClaimText, registerClaimReactHost } from './append.js';
import { batch } from './signal-core.js';
import { createHostElement } from '../../shared/element-create-rules.js';
/**
 * _claimMount — the whole lean hydrate-or-mount flow for a PROVEN compiled
 * root, lowered at the mount site (EPIC-MOON-002 R4b).
 *
 * `hydrateRoot(root, <App/>)` built a React element just to hand it to
 * LeanRoot, which immediately unwrapped it — retaining jsx-runtime and the
 * whole react-dom-client-lean module in every prerender app. When the
 * compiler can prove the root is a compiled plain function (same registry
 * proof as the createRoot inline mount), it emits
 * `__claimMount(container, App, props)` instead. Semantics mirror the lean
 * client: claim server DOM when the container has children; on a mid-claim
 * bail, clear (never on html/body/head) and mount fresh — a mismatch can
 * never leave doubled content.
 */
export function _claimMount(container, comp, props) {
    if (container.hasChildNodes && container.hasChildNodes()) {
        startCompiledHydration(container);
        let dom;
        try {
            batch(() => {
                dom = comp(props);
            });
        }
        finally {
            endCompiledHydration();
        }
        if (!didCompiledHydrationBail()) {
            if (dom instanceof Node && dom.parentNode !== container)
                container.appendChild(dom);
            return;
        }
        const tag = container.tagName && container.tagName.toLowerCase();
        if (tag !== 'html' && tag !== 'body' && tag !== 'head') {
            container.textContent = '';
        }
    }
    batch(() => {
        const dom = comp(props);
        if (dom instanceof Node && dom.parentNode !== container)
            container.appendChild(dom);
    });
}
// --- cursor state ---
let _active = false;
let _queue = [];
let _idx = 0;
let _bailed = false;
/**
 * Nodes that own the top of this pass (the root container, or the parents of a
 * scoped pass's seeded nodes). They are claimed by definition but are NOT in
 * `_queue`, so `_isClaimedParent` has to know them explicitly.
 */
let _passRoots = [];
/**
 * Membership test for `_queue` (BUG-SSR-014). The recovery paths ask "is this
 * DOM node server content or something the client just made?" once per claim, so
 * it has to be O(1); a linear `_queue.indexOf` there is quadratic on a big page.
 * Kept in lockstep with `_queue`: every splice on one is a change to the other.
 */
let _queued = new WeakSet();
/** Server subtrees dropped this pass (reported once at pass end). */
let _resynced = 0;
function _flatten(root, out) {
    for (let n = root.firstChild; n; n = n.nextSibling) {
        if (n.nodeType === 1 || n.nodeType === 3) {
            out.push(n);
            if (n.nodeType === 1)
                _flatten(n, out);
        }
        // comments in server HTML are ignored (our renderer emits none)
    }
}
/**
 * The ONE place a claim pass starts: cursor state + EVERY hook registration.
 *
 * There are two entry points (root and scoped), and for a while they each
 * registered their own hooks. That is the "lockstep by comment" bug class this
 * repo keeps re-learning: adding a hook to one and not the other merges clean
 * and regresses silently in exactly one path. Per the registration-slots
 * doctrine in DANGER-ZONES, **an unregistered slot must be impossible, not
 * remembered** - so a pass start is state init plus all registrations, in one
 * function, and a new hook has exactly one place to go.
 *
 * Registered per pass (not at module top level) so non-ssr bundles, which never
 * import this module, keep appendCompiled's hooks null (zero-k), and the
 * `sideEffects` tree-shake allowlist needs no entry.
 */
function _startPass(queue, roots) {
    _queue = queue;
    _idx = 0;
    _active = true;
    _bailed = false;
    _passRoots = roots;
    _resynced = 0;
    _queued = new WeakSet(queue);
    // Text children rendered through appendCompiled (dynamic/param children built
    // inside effect fragments) never call the $h helpers directly: this hook lets
    // them adopt the server's text node in place.
    registerClaimText(_claimAppendText);
    // HOST React-element children (BUG-SSR-013 symptom 2): consume the server's
    // subtree at the cursor so the following compiled claim does not bail on a
    // node the compat host path was never going to claim.
    registerClaimReactHost(_claimAppendHost);
}
export function startCompiledHydration(container) {
    const queue = [];
    _flatten(container, queue);
    _startPass(queue, [container]);
}
/**
 * Consume the server rendering of a HOST React-element child rendered
 * through appendCompiled (which has no $h claim helper). Requirements
 * mirror _el$h (skip resource links + whitespace text, tag match) plus a
 * parent check: the cursor node must be a direct child of the append
 * target - inner nodes of a FRESH compat subtree land in fresh parents
 * and must never steal a later sibling's server nodes. The node and its
 * whole flattened subtree leave the claim queue and the DOM; the caller
 * re-inserts the fresh client subtree at the returned anchor.
 */
function _claimAppendHost(parent, tag) {
    if (!_active)
        return null;
    if (tag.toLowerCase() !== 'link')
        _skipResourceLinks();
    while (_peek() && _peek().nodeType === 3 && !(/\S/.test(_peek().data)))
        _idx++;
    const n = _peek();
    // Children effects assemble into a DocumentFragment before inserting at
    // their marker (the marker was placed at the cursor, i.e. exactly where
    // the server node sits) - mirror the text hook's "fragment parents adopt"
    // rule. Element parents keep the strict child check: inner nodes of a
    // FRESH compat subtree append into fresh elements and must never steal a
    // later sibling's server nodes.
    const fragParent = parent.nodeType === 11;
    if (n &&
        n.nodeType === 1 &&
        (fragParent || n.parentNode === parent) &&
        n.tagName.toLowerCase() === tag.toLowerCase()) {
        const count = 1 + _descendantCount(n);
        for (let i = 0; i < count; i++)
            if (_queue[_idx + i])
                _queued.delete(_queue[_idx + i]);
        _queue.splice(_idx, count);
        const anchor = fragParent ? null : n.nextSibling;
        if (n.parentNode)
            n.parentNode.removeChild(n);
        return { anchor };
    }
    return null;
}
/**
 * Claim the cursor's text node for an appendCompiled text child. Requires a
 * content match (exact, or server-merged prefix - split like _t$h) so
 * client-only content falls through to fresh creation instead of stealing an
 * unrelated server node; a genuine mismatch then recovers via the normal
 * bail/leftover paths.
 */
function _claimAppendText(data) {
    if (!_active || data === '')
        return null;
    const n = _peek();
    if (n && n.nodeType === 3) {
        const tn = n;
        if (tn.data === data) {
            _idx++;
            return tn;
        }
        if (tn.data.startsWith(data)) {
            const tail = tn.splitText(data.length);
            _queue.splice(_idx + 1, 0, tail);
            _queued.add(tail);
            _idx++;
            return tn;
        }
    }
    return null;
}
export function isCompiledHydrating() {
    return _active;
}
/**
 * Mixed seam (docs/SSR.md contract cell I): a COMPILED component rendering
 * under a COMPAT parent.
 *
 * `startCompiledHydration` seeds the cursor from a container it assumes it owns
 * outright. That assumption holds for a compiled ROOT, but not when compat owns
 * the root and only a subtree is compiled - the shape every Next.js app has,
 * since Next's own App/Root come from node_modules and are never compiled
 * (BUG-NEXT-001). There the compiled body must claim only the slice of server
 * DOM handed to it, and must leave everything else for the compat positional
 * queue.
 *
 * Seeds from an explicit node list rather than a container, and pairs with
 * `endCompiledHydrationScoped`, which never removes leftovers.
 */
export function startCompiledHydrationScoped(nodes) {
    const queue = [];
    const roots = [];
    for (const n of nodes) {
        if (n.nodeType === 1 || n.nodeType === 3) {
            queue.push(n);
            if (n.parentNode && roots.indexOf(n.parentNode) === -1)
                roots.push(n.parentNode);
            if (n.nodeType === 1)
                _flatten(n, queue);
        }
    }
    _startPass(queue, roots);
}
/**
 * End a scoped pass and report which of `topLevel` went unclaimed.
 *
 * Deliberately does NOT remove leftovers, unlike `endCompiledHydration`. In a
 * scoped pass an unclaimed node is not necessarily stale: it may belong to a
 * later sibling that the compat reconciler is about to claim. Removing it there
 * would delete live server content. The caller hands the returned nodes back to
 * the compat queue.
 */
export function endCompiledHydrationScoped(topLevel) {
    const claimed = new Set();
    for (let i = 0; i < _idx && i < _queue.length; i++)
        claimed.add(_queue[i]);
    _reportResyncs();
    _active = false;
    _queue = [];
    _idx = 0;
    return topLevel.filter((n) => !claimed.has(n));
}
/**
 * Whether the last pass hit a structural mismatch and deactivated claiming.
 * Valid after endCompiledHydration (start resets it); callers use it to fall
 * back to a full clear + client re-render (React's mismatch recovery).
 */
export function didCompiledHydrationBail() {
    return _bailed;
}
/**
 * End the pass. Any unclaimed leftovers are server content the client render
 * did not produce: warn and remove them (client wins, compat parity).
 * Returns the number of leftovers for callers/tests.
 */
export function endCompiledHydration() {
    let leftovers = 0;
    if (_active && !_bailed) {
        const claimedParents = new Set();
        for (let i = _idx; i < _queue.length; i++) {
            const n = _queue[i];
            // Skip descendants of nodes we are already removing.
            if (n.parentNode && claimedParents.has(n.parentNode))
                continue;
            if (n.parentNode) {
                leftovers++;
                claimedParents.add(n);
                n.parentNode.removeChild(n);
            }
        }
        if (leftovers > 0) {
            console.error(`Hydration mismatch: ${leftovers} server-rendered node(s) were not claimed by the client render and were removed.`);
        }
    }
    _reportResyncs();
    _active = false;
    _queue = [];
    _idx = 0;
    return leftovers;
}
/**
 * One dev-only line per pass naming how much of the server DOM the claim cursor
 * had to throw away to stay aligned (BUG-SSR-014). A silent recovery is how a
 * structural gap in the claim path turns into "hydration is fine, actually" - so
 * whatever `_skipReplaced` / `_resync` drop is stated, once, with the count.
 */
function _reportResyncs() {
    if (_resynced > 0 && typeof __DEV__ !== 'undefined' && __DEV__) {
        console.warn(`Compiled hydration re-synced past ${_resynced} server subtree(s) the client re-rendered ` +
            `instead of claiming (compat components under a compiled parent). Hydration completed; ` +
            `those subtrees lost node identity.`);
    }
    _resynced = 0;
}
function _bail(reason) {
    if (!_bailed) {
        _bailed = true;
        _active = false;
        console.error(`Compiled hydration bailed: ${reason}. Falling back to client rendering for the rest of this tree.`);
    }
}
/** Next queue node without consuming. */
function _peek() {
    return _idx < _queue.length ? _queue[_idx] : null;
}
/**
 * Skip hoisted resource links. renderToStaticMarkup prepends React Float
 * <link rel="preload"> hints (img hoisting) that no client render creates.
 * They are hydration-neutral: advance past them (leaving them in the DOM,
 * the hint already fired) whenever the claim target is not itself a <link>.
 */
function _skipResourceLinks() {
    let n = _peek();
    while (n && n.nodeType === 1 && n.tagName === 'LINK') {
        _idx++;
        n = _peek();
    }
}
/** Flattened (element/text, pre-order) descendant count of `root` - the
 * number of queue slots its subtree occupies. */
function _descendantCount(root) {
    let count = 0;
    for (let c = root.firstChild; c; c = c.nextSibling) {
        if (c.nodeType === 1 || c.nodeType === 3) {
            count++;
            if (c.nodeType === 1)
                count += _descendantCount(c);
        }
    }
    return count;
}
/**
 * BUG-SSR-014 - the server DOM of a COMPAT-rendered COMPONENT child.
 *
 * `registerClaimReactHost` consumes the server rendering of a HOST React-element
 * child of a compiled parent (`<p>{x}</p>` passed through a children prop). It
 * cannot fire for a COMPONENT-typed child (`<BreadcrumbLink>`, `<Separator>`):
 * the tag such a child will render is unknown until the compat path has rendered
 * it, so `appendCompiled` has nothing to match on and renders fresh. Nothing then
 * consumes the server's rendering of that component, and the claim cursor stays
 * parked on it. Composition makes this the common case rather than a corner:
 * shadcn wraps a handful of components in Base UI's `useRender`, so a compiled
 * page hands them down as component children.
 *
 * There are TWO failure modes, and the quiet one is the dangerous one:
 *
 *  - the next compiled claim wants a DIFFERENT tag -> `_bail`, and the whole tree
 *    falls back to a client render (the reported symptom: composed-prerender died
 *    at queue index 4 of 107, the `<a>` inside the first breadcrumb `<li>`);
 *  - the next compiled claim wants the SAME tag -> no bail at all. The cursor
 *    claims the WRONG node, and every claim after it is off by one: props are
 *    written onto the neighbour, `_ap$h` re-parents adopted nodes, and the page
 *    is silently corrupted. Measured on this page with claiming forced past the
 *    first fault: `<Separator>` (compat) left its server `<div>` behind, so
 *    `<Card>` claimed the separator, `<CardHeader>` claimed the card,
 *    `<CardContent>` claimed the description, and so on down the subtree.
 *
 * Both come from the same missing consume, and both are recovered here from
 * evidence in the DOM rather than from a guess about what the compat path did.
 *
 * ORDER, the rule for `_skipReplaced` (runs BEFORE the tag test, so it catches
 * the quiet mode). Claiming is pre-order: everything the client has produced for
 * a claimed parent so far sits BEFORE the cursor - adopted nodes never move, and
 * `_ap$h` inserts fresh nodes AT the cursor. So a fresh node sitting AFTER the
 * cursor inside the cursor's own parent can only be a compat mount that was
 * `appendChild`-ed past it: proof that the client already rendered the
 * replacement for this server node and is not going to claim it.
 *
 * TAG, the rule for `_resync` (runs only once the tag test has already failed,
 * i.e. where today's code bails outright). Weaker evidence is enough there
 * because the alternative is losing the whole tree: any fresh node in the claimed
 * parent means the client rendered into it, so a server child it walked past is
 * stale.
 *
 * Dropping re-anchors first. A compat mount lands at the END of the parent, so
 * removing the server node alone would leave the fresh subtree stranded behind
 * later siblings; the trailing non-queued run moves into the dropped node's slot,
 * the same re-anchoring `_claimAppendHost` does with its `anchor`. The cost is
 * one subtree's node identity (client wins - React's own mismatch-recovery
 * semantics) instead of the whole tree's.
 */
function _skipReplaced() {
    for (;;) {
        const n = _peek();
        if (!n)
            return;
        const parent = n.parentNode;
        if (!parent || !_isClaimedParent(parent))
            return;
        // A trailing run of non-queued nodes is the only way a fresh node can sit
        // after the cursor, so an already-queued last child means "nothing to do" in
        // O(1) - which is the answer for almost every claim.
        if (!_freshTail(parent, n))
            return;
        _drop(n, parent);
    }
}
function _resync(matches) {
    for (;;) {
        const n = _peek();
        if (!n)
            return false;
        if (matches(n))
            return true;
        const parent = n.parentNode;
        if (!parent || !_isClaimedParent(parent))
            return false;
        let fresh = false;
        for (let c = parent.firstChild; c && !fresh; c = c.nextSibling) {
            if ((c.nodeType === 1 || c.nodeType === 3) && !_queued.has(c))
                fresh = true;
        }
        if (!fresh)
            return false;
        _drop(n, parent);
    }
}
/**
 * The parent's trailing run of client-created nodes, or null when there is none
 * (or when it does not reach past `n`, i.e. nothing was appended past the
 * cursor). Comments count as part of a run but never start one: a lone marker is
 * not evidence that a subtree was replaced.
 */
function _freshTail(parent, n) {
    let seen = false;
    const run = [];
    for (let c = parent.lastChild; c && c !== n; c = c.previousSibling) {
        if (_queued.has(c))
            return null;
        if (c.nodeType === 1 || c.nodeType === 3)
            seen = true;
        run.unshift(c);
    }
    return seen ? run : null;
}
/** Remove a superseded server node: re-anchor the client's replacement into its
 * slot, then take it out of the DOM and the claim queue with its whole subtree. */
function _drop(n, parent) {
    const tail = _freshTail(parent, n);
    const anchor = n.nextSibling;
    if (tail && anchor && tail.indexOf(anchor) === -1) {
        for (const f of tail)
            parent.insertBefore(f, anchor);
    }
    const count = 1 + _descendantCount(n);
    for (let i = 0; i < count; i++) {
        const gone = _queue[_idx + i];
        if (gone)
            _queued.delete(gone);
    }
    _queue.splice(_idx, count);
    parent.removeChild(n);
    _resynced++;
}
/** Was `p` adopted by this pass (or is it the node the pass was seeded from)? */
function _isClaimedParent(p) {
    // `p` is an ancestor of the cursor node, so in the flattened pre-order queue it
    // precedes the cursor - being IN the queue at all therefore means "already
    // claimed". A parent that is not in the queue is either a fresh client element
    // or the node this pass was seeded from.
    return _queued.has(p) || _passRoots.indexOf(p) !== -1;
}
/**
 * Insertion point for FRESH nodes (markers, client-only content): before the
 * next unclaimed node when it sits in `parent`, else append.
 */
function _insertAtCursor(parent, node) {
    const next = _peek();
    if (next && next.parentNode === parent) {
        parent.insertBefore(node, next);
    }
    else {
        parent.appendChild(node);
    }
}
/** Is `n` an element with this tag name? */
function _isTag(n, tag) {
    return !!n && n.nodeType === 1 && n.tagName.toLowerCase() === tag.toLowerCase();
}
// --- $h creation helpers (codegen swap targets) ---
export function _el$h(tag) {
    if (_active) {
        if (tag.toLowerCase() !== 'link')
            _skipResourceLinks();
        // Advance past any whitespace-only text the compiled body will not claim.
        while (_peek() && _peek().nodeType === 3 && !(/\S/.test(_peek().data)))
            _idx++;
        // BUG-SSR-014: the cursor may be parked on the server rendering of a
        // COMPONENT child the compat path already rebuilt. This must run BEFORE the
        // tag test - a same-tag neighbour would otherwise be claimed as if it were
        // ours and take the whole rest of the tree off by one, silently.
        _skipReplaced();
        let n = _peek();
        if (!_isTag(n, tag)) {
            _resync((c) => _isTag(c, tag));
            n = _peek();
        }
        if (_isTag(n, tag)) {
            _idx++;
            // Deferred hydration boundary (TASK-SSR-002): the server rendered
            // content inside this host that the client will adopt LATER through a
            // nested hydrateRoot (lazy-route shells). Leave the subtree in the DOM
            // and walk the cursor past it so neither the next claim nor the
            // leftover sweep touches it. Contract: the compiled body of the
            // claiming component renders NO children into this host.
            if (n.getAttribute('data-jethydrate') === 'defer') {
                _idx += _descendantCount(n);
            }
            return n;
        }
        _bail((typeof __DEV__ !== 'undefined' && __DEV__) ? `expected <${tag}>, found ${n ? (n.nodeType === 1 ? '<' + n.tagName.toLowerCase() + '>' : 'text "' + String(n.data).slice(0, 40) + '"') : 'end of server DOM'}` : 'element mismatch');
    }
    // BUG-RUNTIME-141: same inert-creation rule as `_el`.
    return createHostElement(document, tag);
}
export function _svg$h(tag) {
    if (_active) {
        const n = _peek();
        if (n && n.nodeType === 1 && n.tagName.toLowerCase() === tag.toLowerCase()) {
            _idx++;
            return n;
        }
        _bail((typeof __DEV__ !== 'undefined' && __DEV__) ? `expected <${tag}> (svg), found ${n ? 'other node' : 'end of server DOM'}` : 'svg mismatch');
    }
    return document.createElementNS('http://www.w3.org/2000/svg', tag);
}
/** Static text: claim exact text, splitting a longer server text node. */
export function _t$h(parent, text) {
    if (_active) {
        _skipResourceLinks();
        const n = _peek();
        if (n && n.nodeType === 3 && n.parentNode === parent) {
            const t = n;
            if (t.data === text) {
                _idx++;
                return;
            }
            if (t.data.startsWith(text)) {
                // The server merged adjacent text; split and queue the tail in place.
                const tail = t.splitText(text.length);
                _queue.splice(_idx + 1, 0, tail);
                _queued.add(tail);
                _idx++;
                return;
            }
            _bail((typeof __DEV__ !== 'undefined' && __DEV__) ? `text mismatch: server "${t.data.slice(0, 40)}", client "${text.slice(0, 40)}"` : 'text mismatch');
        }
        else if (n || _idx >= _queue.length) {
            _bail((typeof __DEV__ !== 'undefined' && __DEV__) ? `expected text "${text.slice(0, 40)}", found ${n ? 'a non-text node' : 'end of server DOM'}` : 'text mismatch');
        }
    }
    parent.appendChild(document.createTextNode(text));
}
/**
 * Dynamic text slot: claim the next text node (its data is the server's
 * rendering of the same signal value; the slot effect re-writes .data with
 * the identical value). Returns a node NOT yet appended - assembly runs
 * through _ap$h, which no-ops for claimed nodes.
 */
export function _tn$h(text) {
    if (_active) {
        _skipResourceLinks();
        const n = _peek();
        if (n && n.nodeType === 3) {
            const tn = n;
            // Codegen passes the slot's initial display value (piece 158): when
            // the server merged this slot with FOLLOWING static text ("3 of 10"),
            // split at the value boundary and re-queue the tail so the next
            // static/dynamic claim finds its node. Exact match and legacy ''
            // callers claim the whole node (the slot effect rewrites .data).
            if (text !== '' && tn.data !== text && tn.data.startsWith(text)) {
                const tail = tn.splitText(text.length);
                _queue.splice(_idx + 1, 0, tail);
                _queued.add(tail);
            }
            _idx++;
            return tn;
        }
        // A dynamic slot rendering '' produces no server text node - that is not
        // a mismatch; create the empty text fresh (positioned by _ap$h).
        if (text === '')
            return document.createTextNode(text);
        _bail((typeof __DEV__ !== 'undefined' && __DEV__) ? `expected a text node for a dynamic slot, found ${n ? 'a non-text node' : 'end of server DOM'}` : 'slot mismatch');
    }
    return document.createTextNode(text);
}
/** Markers never exist server-side: create fresh (positioned by _ap$h). */
export function _cm$h(text) {
    return document.createComment(text || '');
}
/**
 * dangerouslySetInnerHTML assignment. A claimed element (in the server DOM)
 * already holds the server's rendering of the same html string: consume its
 * queued descendants (an innerHTML element has no other children to claim)
 * and only rewrite when the content genuinely differs (client wins). Fresh
 * elements and post-hydration effect re-runs assign as always.
 */
export function _ih$h(el, html) {
    if (_active && el.parentNode) {
        _idx += _descendantCount(el);
        if (el.innerHTML === html)
            return;
    }
    el.innerHTML = html;
}
/**
 * Assembly append. Claimed children are already in position: no-op. Fresh
 * nodes (markers, client-only content) insert at the cursor position so
 * ordering matches the client render exactly.
 */
export function _ap$h(parent, child) {
    if (_active) {
        if (child.parentNode === parent)
            return; // claimed, already placed
        _insertAtCursor(parent, child);
        return;
    }
    parent.appendChild(child);
}
/**
 * Branch template instantiation: claim the server's branch content when it
 * matches the template root; otherwise clone fresh (e.g. the server rendered
 * the other branch - the branch effect's normal swap logic takes over).
 */
export function _cl$h(tmplRoot) {
    if (_active) {
        const n = _peek();
        if (n && tmplRoot.nodeType === 1 && n.nodeType === 1 &&
            n.tagName === tmplRoot.tagName) {
            _idx++;
            // Descendants of a fully static template are claimed wholesale: skip
            // the queue past them (static content has no per-node claim calls).
            _idx += _descendantCount(n);
            return n;
        }
        // Not a mismatch worth bailing the whole tree: the branch machinery
        // handles insertion of fresh content correctly.
    }
    return tmplRoot.cloneNode(true);
}
/**
 * Brand a compiler-emitted ELEMENT-CHILDREN thunk (TASK-SSR-002). Under the
 * plugin ssr flag, codegen lowers a component call's host-element children
 * into `__ec$(() => { ...create...; return nodes; })` instead of creating
 * them eagerly BEFORE the component call, so client creation order matches
 * server pre-order (the receiver invokes the thunk via appendCompiled at the
 * exact flow position of `{children}` in its body). Two brands:
 * - `_isSignalGetter`: appendCompiled must PROPAGATE throws from this thunk.
 *   It is compiler-emitted render code, so a throw during evaluation is a
 *   real render error (error-boundary semantics) or a thrown promise
 *   (Suspense/lazy) and must reach the enclosing catch - the unbranded
 *   function-child path swallows throws by design (BUG-COMPILER-019).
 * - `_jetElChildren`: _resolveFunctionProps (render-component.ts) resolves
 *   the thunk to its DOM nodes when the receiver dispatches to a COMPAT
 *   path, which received eagerly-created DOM nodes for exactly these shapes
 *   before this lowering existed.
 */
export function _ec$h(fn) {
    fn._isSignalGetter = true;
    fn._jetElChildren = true;
    return fn;
}
/**
 * Static hole (TASK-HF-001 automatic mix): claim a prerendered static
 * subtree wholesale, or rebuild it from the baked HTML string.
 *
 * The mix build replaces a provably-static component module with a stub
 * whose component is `_holeComponent(html)`. When the compiled parent
 * renders it during hydration, the server's element (and every descendant)
 * is adopted in place — no factory code for the subtree ships. Outside
 * hydration (claim bail, conditional re-mount, client-only render) the DOM
 * is built from the HTML string: data, not executable component code.
 */
export function _hole$h(html) {
    if (_active) {
        _skipResourceLinks();
        // Advance past whitespace-only text the hole will not claim.
        while (_peek() && _peek().nodeType === 3 && !(/\S/.test(_peek().data)))
            _idx++;
        const n = _peek();
        if (n && n.nodeType === 1) {
            // Cheap structural check: the claimed root's tag must match the baked
            // HTML's root tag, else this is not our subtree — bail (fresh render
            // rebuilds everything, including this hole from its string).
            const t = document.createElement('template');
            t.innerHTML = html;
            const baked = t.content.firstElementChild;
            if (baked && baked.tagName === n.tagName) {
                _idx++;
                _idx += _descendantCount(n);
                return n;
            }
            _bail((typeof __DEV__ !== 'undefined' && __DEV__) ? `static hole expected <${baked ? baked.tagName.toLowerCase() : '?'}>, found <${n.tagName.toLowerCase()}>` : 'hole mismatch');
        }
        else {
            _bail((typeof __DEV__ !== 'undefined' && __DEV__) ? `static hole expected an element, found ${n ? 'text' : 'end of server DOM'}` : 'hole mismatch');
        }
    }
    const tmpl = document.createElement('template');
    tmpl.innerHTML = html;
    const root = tmpl.content.firstElementChild;
    return root ? root : document.createTextNode('');
}
/**
 * Build the stub component for a static hole. Branded `_isCompiled` so both
 * parent call paths treat it as a compiled component: the direct-call /
 * renderCompiledComponent path invokes it with raw props (ignored — the
 * literal props are already baked into the HTML) and appends the returned
 * Node; appendCompiled receives a plain Node.
 */
export function _holeComponent(html) {
    let Hole;
    if (typeof html === 'string') {
        Hole = (_props) => _hole$h(html);
    }
    else {
        // TASK-BASECOAT-013: multi-signature hole. Key derivation MUST mirror
        // mix-scan's signature key: sorted-key JSON of props minus children,
        // a space, then JSON of the normalized static-text children (null when
        // absent). The hydration claim path is signature-agnostic (_hole$h
        // claims whatever the server rendered at the cursor); the table only
        // matters for fresh non-hydrating mounts. A miss falls back to the
        // lone entry when there is exactly one, else renders nothing (the scan
        // enumerated every call site, so a miss is a bug, not a user state).
        const keys = Object.keys(html);
        Hole = (props) => {
            const { children, ...rest } = props || {};
            const norm = typeof children === 'string' || typeof children === 'number'
                ? String(children).replace(/\s+/g, ' ').trim()
                : null;
            const key = JSON.stringify(rest, Object.keys(rest).sort()) + ' ' + JSON.stringify(norm);
            const h = html[key];
            if (h != null)
                return _hole$h(h);
            if (keys.length === 1)
                return _hole$h(html[keys[0]]);
            return null;
        };
    }
    Hole._isCompiled = true;
    Hole._jetStaticHole = true;
    return Hole;
}
/** Conditional/list content insertion: skip when content was claimed. */
export function _ins$h(marker, content) {
    if (_active && content.nodeType !== 11 && content.parentNode) {
        return; // claimed and already in position
    }
    marker.parentNode.insertBefore(content, marker);
}
